import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend } from 'recharts';
import { CATEGORICAL_COLORS } from './chartUtils';

const RADIAN = Math.PI / 180;

const renderInsideLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    const radius = innerRadius + (outerRadius - innerRadius) / 2;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    const isTiny = percent < 0.04;
    return (
        <text
            x={x}
            y={y}
            fill={isTiny ? '#1e1e1e' : '#ffffff'}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={isTiny ? 11 : 12}
            fontWeight={700}
            style={{
                paintOrder: 'stroke',
                stroke: isTiny ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.35)',
                strokeWidth: isTiny ? 3 : 2,
            }}
        >
            {`${Math.round(percent * 100)}%`}
        </text>
    );
};

// Generic top-N donut breakdown. `data` items: `{name, count, successRate?}`
// (successRate is 0-1 and shown in the tooltip when provided).
const CategoryPieChart = ({ data: rawData, expanded, unitLabel = 'רשומות', topN = 8, fallbackName = 'לא ידוע' }) => {
    const data = useMemo(() => {
        return [...(rawData || [])]
            .sort((a, b) => b.count - a.count)
            .slice(0, topN)
            .map((s) => ({
                name: s.name || fallbackName,
                count: s.count,
                successRate: s.successRate !== undefined ? Math.round(s.successRate * 100) : null,
            }));
    }, [rawData, topN, fallbackName]);

    const total = useMemo(() => data.reduce((sum, d) => sum + d.count, 0), [data]);
    const height = expanded ? 400 : 300;
    const cyPercent = 44;

    if (!data.length) {
        return <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>אין נתונים לטווח שנבחר</Typography>;
    }

    return (
        <Box sx={{ position: 'relative' }}>
            <ResponsiveContainer width="100%" height={height}>
                <PieChart>
                    <Pie
                        data={data}
                        dataKey="count"
                        nameKey="name"
                        cy={`${cyPercent}%`}
                        innerRadius="48%"
                        outerRadius="72%"
                        paddingAngle={2}
                        stroke="#fcfcfb"
                        strokeWidth={2}
                        label={renderInsideLabel}
                        labelLine={false}
                        isAnimationActive={false}
                    >
                        {data.map((entry, index) => (
                            <Cell key={entry.name} fill={CATEGORICAL_COLORS[index % CATEGORICAL_COLORS.length]} />
                        ))}
                    </Pie>
                    <Tooltip
                        formatter={(value, name, item) => [
                            item.payload.successRate !== null
                                ? `${value} ${unitLabel} · ${item.payload.successRate}% הצליחו`
                                : `${value} ${unitLabel}`,
                            item.payload.name,
                        ]}
                    />
                    <Legend verticalAlign="bottom" iconType="circle" wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                </PieChart>
            </ResponsiveContainer>

            <Box
                sx={{
                    position: 'absolute',
                    top: `calc(${cyPercent}% - 14px)`,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    textAlign: 'center',
                    pointerEvents: 'none',
                }}
            >
                <Typography variant="h6" fontWeight={700} lineHeight={1}>{total.toLocaleString('he-IL')}</Typography>
                <Typography variant="caption" color="text.secondary">סה״כ</Typography>
            </Box>
        </Box>
    );
};

export default CategoryPieChart;
